import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

/**
 * BrandingContext — global hospital identity (name, tagline, address, phones,
 * logo). Fetched once at app boot from /api/branding (public endpoint) so
 * every page — including unauthenticated ones like /kiosk and print pages —
 * can render up-to-date branding without redeploying.
 */
const BrandingContext = createContext({
  name: "DIVINE HOSPITAL",
  short_name: "Divine Hospital",
  tagline: "MULTI SPECIALITY HOSPITAL",
  address: "",
  phones: [],
  email: "",
  website: "",
  abdm_enabled: false,
  logo_url: `${process.env.REACT_APP_BACKEND_URL}/api/branding/logo`,
  loading: true,
  reload: async () => {},
});

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function BrandingProvider({ children }) {
  const [state, setState] = useState({
    name: "DIVINE HOSPITAL",
    short_name: "Divine Hospital",
    tagline: "MULTI SPECIALITY HOSPITAL",
    address: "",
    phones: [],
    email: "",
    website: "",
    abdm_enabled: false,
    logo_url: `${BACKEND}/api/branding/logo`,
    loading: true,
  });

  const reload = useCallback(async () => {
    try {
      // Pass the current hostname so the backend can auto-resolve the
      // tenant when we're on a subdomain like `testb.d-homs.online` —
      // print pages therefore never show another hospital's letterhead.
      const host = typeof window !== "undefined" ? window.location.hostname : "";
      const { data } = await axios.get(`${BACKEND}/api/branding`, { params: { host } });
      setState({
        ...data,
        logo_url: `${BACKEND}${data.logo_url}`,
        loading: false,
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const value = useMemo(() => ({ ...state, reload }), [state, reload]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
